import { FC, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import {
	Alert,
	Box,
	Button,
	Container,
	ContentLayout,
	Header,
	SpaceBetween
} from '@cloudscape-design/components'

import LoadingSpinner from '../../../components/LoadingSpinner'
import { AdGitOpsCallbackResponse } from '../../../api/active-directory/adGitOpsCallbacks'

interface AuthenticatedAdGitOpsCallbackPageProps {
	title: string
	description: string
	onConfirm: (queryString: string) => Promise<AdGitOpsCallbackResponse>
	unauthorizedMessage?: string
	expiredMessage?: string
}

interface StoredCallbackQuery {
	pathname: string
	search: string
	savedAt: number
}

interface CallbackDisplay {
	type: 'success' | 'info' | 'warning' | 'error'
	header: string
	message: string
}

const CALLBACK_QUERY_TTL_MS = 10 * 60 * 1000

const getCallbackQueryStorageKey = (pathname: string) =>
	`adgitops-callback-query:${pathname}`

const readStoredCallbackSearch = (storageKey: string, pathname: string) => {
	const storedValue = window.sessionStorage.getItem(storageKey)
	if (!storedValue) {
		return ''
	}

	try {
		const storedQuery = JSON.parse(storedValue) as StoredCallbackQuery
		const isExpired = Date.now() - storedQuery.savedAt > CALLBACK_QUERY_TTL_MS
		if (
			isExpired ||
			storedQuery.pathname !== pathname ||
			!storedQuery.search.startsWith('?')
		) {
			window.sessionStorage.removeItem(storageKey)
			return ''
		}

		return storedQuery.search
	} catch {
		window.sessionStorage.removeItem(storageKey)
		return ''
	}
}

const storeCallbackSearch = (
	storageKey: string,
	pathname: string,
	search: string
) => {
	if (!search.startsWith('?')) {
		return
	}

	const storedQuery: StoredCallbackQuery = {
		pathname,
		search,
		savedAt: Date.now()
	}
	window.sessionStorage.setItem(storageKey, JSON.stringify(storedQuery))
}

const getCallbackDisplay = (
	response: AdGitOpsCallbackResponse | null,
	error: string | null,
	unauthorizedMessage: string,
	expiredMessage: string
): CallbackDisplay | null => {
	if (error) {
		return {
			type: 'error',
			header: 'Callback failed',
			message: error
		}
	}

	if (!response) {
		return null
	}

	const html = response.html.toLowerCase()

	if (response.ok) {
		if (html.includes('already completed') || html.includes('already recorded')) {
			return {
				type: 'info',
				header: 'Already completed',
				message: 'This request has already been processed. No further action is needed.'
			}
		}

		return {
			type: 'success',
			header: 'Request completed',
			message: 'Your confirmation was accepted and the request is being processed.'
		}
	}

	if (html.includes('invalid') || html.includes('tampered')) {
		return {
			type: 'warning',
			header: 'Link invalid',
			message:
				'This link is invalid or may have been tampered with. Please use the original email link or request a new one.'
		}
	}

	if (html.includes('expired')) {
		return {
			type: 'warning',
			header: 'Link expired',
			message: expiredMessage
		}
	}

	if (
		response.status === 401 ||
		response.status === 403 ||
		html.includes('unauthorized') ||
		html.includes('not authorized') ||
		html.includes('permission') ||
		html.includes('only valid')
	) {
		return {
			type: 'warning',
			header: 'Not authorized',
			message: unauthorizedMessage
		}
	}

	return {
		type: 'error',
		header: 'Request unavailable',
		message:
			'This request could not be completed right now. Please try again later or contact the platform team.'
	}
}

const AuthenticatedAdGitOpsCallbackPage: FC<
	AuthenticatedAdGitOpsCallbackPageProps
> = ({
	title,
	description,
	onConfirm,
	unauthorizedMessage = 'You are signed in, but this link is only available to the intended recipient with the required SSP permission.',
	expiredMessage = 'This link has expired. Please request a fresh approval email.'
}) => {
	const { pathname, search } = useLocation()
	const [isLoading, setIsLoading] = useState(true)
	const [response, setResponse] = useState<AdGitOpsCallbackResponse | null>(null)
	const [error, setError] = useState<string | null>(null)

	useEffect(() => {
		let cancelled = false
		const storageKey = getCallbackQueryStorageKey(pathname)

		const confirm = async () => {
			setIsLoading(true)
			setError(null)

			try {
				if (search) {
					storeCallbackSearch(storageKey, pathname, search)
				}

				const queryString =
					search || readStoredCallbackSearch(storageKey, pathname)

				if (!queryString) {
					setResponse(null)
					setError(
						'This callback link is missing its signed query parameters. Reopen the full email link and try again.'
					)
					return
				}

				const callbackResponse = await onConfirm(queryString)
				if (!cancelled) {
					setResponse(callbackResponse)
				}
			} catch (callbackError) {
				if (!cancelled) {
					setError(
						callbackError instanceof Error
							? callbackError.message
							: 'The callback request could not be completed.'
					)
				}
			} finally {
				if (!cancelled) {
					setIsLoading(false)
				}
			}
		}

		confirm()

		return () => {
			cancelled = true
		}
	}, [onConfirm, pathname, search])

	if (isLoading) {
		return <LoadingSpinner />
	}

	const callbackDisplay = getCallbackDisplay(
		response,
		error,
		unauthorizedMessage,
		expiredMessage
	)

	return (
		<ContentLayout header={<Header variant="h1">{title}</Header>}>
			<SpaceBetween size="l">
				<Container>
					<SpaceBetween size="m">
						<Box variant="p">{description}</Box>
						{callbackDisplay && (
							<Alert type={callbackDisplay.type} header={callbackDisplay.header}>
								{callbackDisplay.message}
							</Alert>
						)}
						<Button href="/active-directory">Back to Active Directory</Button>
					</SpaceBetween>
				</Container>
			</SpaceBetween>
		</ContentLayout>
	)
}

export default AuthenticatedAdGitOpsCallbackPage
